<template>
  <div
    id="wago-energy-heatmap"
    class="wago-heatmap chart-container chart-container--wago mb-3 px-0"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <div
      id="wago-energy-heatmap-header"
      class="wago-heatmap__header chart-header"
    >
      <h3
        id="wago-energy-heatmap-title"
        class="wago-heatmap__title chart-title"
      >
        <i class="wago-heatmap__title-icon bi bi-grid-3x3-gap-fill" />
        <span class="wago-heatmap__title-text">{{ $t('components.assetDetails.wagoHeatmap.title') }}</span>
      </h3>
      <div
        id="wago-energy-heatmap-actions"
        class="wago-heatmap__actions chart-actions"
      >
        <button
          id="wago-energy-heatmap-export-csv"
          class="wago-heatmap__export-btn btn btn-custom chart-action-export"
          type="button"
          :title="$t('components.assetDetails.wagoHeatmap.exportCsv')"
          @click="exportCsv"
        >
          <i class="wago-heatmap__export-icon bi bi-filetype-csv" />
        </button>
      </div>
    </div>

    <div
      id="wago-energy-heatmap-body"
      class="wago-heatmap__body chart-body"
    >
      <div
        v-if="hours.length === 0"
        id="wago-energy-heatmap-empty"
        class="wago-heatmap__empty heatmap-empty"
      >
        {{ $t('components.assetDetails.noHistory') }}
      </div>

      <div
        v-else
        id="wago-energy-heatmap-scroll"
        class="wago-heatmap__scroll heatmap-scroll-wrapper"
      >
        <table
          id="wago-energy-heatmap-table"
          class="wago-heatmap__table heatmap-table"
        >
          <thead>
            <tr class="wago-heatmap__head-row">
              <th
                id="wago-energy-heatmap-series-col"
                class="wago-heatmap__series-col heatmap-series-col"
              >
                {{ $t('components.assetDetails.seriesTable.series') }}
              </th>
              <th
                v-for="hour in hours"
                :id="getHourHeaderId(hour)"
                :key="hour"
                class="wago-heatmap__hour-col heatmap-hour-col"
              >
                {{ formatHour(hour) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="series in energySeries"
              :id="getSeriesRowId(series.key)"
              :key="series.key"
              class="wago-heatmap__series-row"
            >
              <td class="wago-heatmap__series-col wago-heatmap__series-label heatmap-series-col heatmap-series-label">
                <span
                  class="wago-heatmap__series-dot heatmap-series-dot"
                  :style="{ background: series.dotColor }"
                />
                {{ series.label }}
              </td>
              <td
                v-for="hour in hours"
                :id="getCellId(series.key, hour)"
                :key="hour"
                class="wago-heatmap__cell heatmap-cell"
                :style="cellStyle(series.key, hour)"
                :title="cellTitle(series.key, hour, series.unit)"
              >
                <span
                  v-if="getCellDelta(series.key, hour) !== null"
                  class="wago-heatmap__cell-value heatmap-cell-value"
                >
                  {{ formatValue(getCellDelta(series.key, hour)!, series.unit) }}
                </span>
                <span
                  v-else
                  class="wago-heatmap__no-data heatmap-no-data"
                >—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import dayjs from 'dayjs'
import { type ChartSeriesRecord, type SeriesKey } from '@/utils/asset-history-series'

const ENERGY_SERIES: { key: SeriesKey; labelKey: string; unit: string; dotColor: string }[] = [
  { key: 'wagoEnergyActive',   labelKey: 'components.assetDetails.wagoLabels.energy_active',   unit: 'kWh',   dotColor: 'rgba(16, 185, 129, 0.85)' },
  { key: 'wagoEnergyReactive', labelKey: 'components.assetDetails.wagoLabels.energy_reactive', unit: 'kvarh', dotColor: 'rgba(59, 130, 246, 0.85)' }
]

const HOUR_MS = 3_600_000

// Only the last reading per hour matters for a cumulative counter
type HourBucket = { last: number }

export default defineComponent({
  name: 'WagoEnergyHeatmap',
  props: {
    chartSeries: {
      type: Object as PropType<ChartSeriesRecord>,
      required: true
    },
    isDarkMode: {
      type: Boolean,
      default: false
    },
    rangeStartTs: {
      type: Number as PropType<number | null>,
      default: null
    },
    rangeEndTs: {
      type: Number as PropType<number | null>,
      default: null
    }
  },
  computed: {
    energySeries(): { key: SeriesKey; label: string; unit: string; dotColor: string }[] {
      return ENERGY_SERIES.map((s) => ({
        key: s.key,
        label: this.$t(s.labelKey) as string,
        unit: s.unit,
        dotColor: s.dotColor
      }))
    },

    // Last reading per hour bucket — points arrive sorted by timestamp ascending
    hourlyData(): Record<SeriesKey, Map<number, HourBucket>> {
      const result = {} as Record<SeriesKey, Map<number, HourBucket>>

      for (const { key } of ENERGY_SERIES) {
        const map = new Map<number, HourBucket>()
        const points = this.chartSeries[key]?.points ?? []

        for (const { x, y } of points) {
          const hourTs = Math.floor(x / HOUR_MS) * HOUR_MS
          map.set(hourTs, { last: y }) // each new point overwrites → last reading wins
        }

        result[key] = map
      }

      return result
    },

    hours(): number[] {
      const hasExplicitRange = Number.isFinite(this.rangeStartTs) && Number.isFinite(this.rangeEndTs)
      if (hasExplicitRange) {
        const startHour = Math.floor((this.rangeStartTs as number) / HOUR_MS) * HOUR_MS
        const endHour = Math.floor((this.rangeEndTs as number) / HOUR_MS) * HOUR_MS
        if (endHour >= startHour) {
          const hours: number[] = []
          for (let hour = startHour; hour <= endHour; hour += HOUR_MS) {
            hours.push(hour)
          }
          return hours
        }
      }

      const allHours = new Set<number>()
      for (const { key } of ENERGY_SERIES) {
        this.hourlyData[key]?.forEach((_, hourTs) => allHours.add(hourTs))
      }
      return Array.from(allHours).sort((a, b) => a - b)
    },
    showDateInHourLabel(): boolean {
      if (this.hours.length <= 1) return false
      return !dayjs(this.hours[0]).isSame(dayjs(this.hours[this.hours.length - 1]), 'day')
    },

    // Per-series: mean and max of positive deltas (for color thresholding)
    deltaStats(): Record<SeriesKey, { mean: number; max: number }> {
      const result = {} as Record<SeriesKey, { mean: number; max: number }>

      for (const { key } of ENERGY_SERIES) {
        const deltas: number[] = []
        for (let i = 1; i < this.hours.length; i++) {
          const d = this.getCellDelta(key, this.hours[i])
          if (d !== null && d > 0) deltas.push(d)
        }
        if (deltas.length === 0) {
          result[key] = { mean: 0, max: 0 }
          continue
        }
        const total = deltas.reduce((a, b) => a + b, 0)
        result[key] = {
          mean: parseFloat((total / deltas.length).toFixed(2)),
          max: Math.max(...deltas)
        }
      }

      return result
    }
  },
  methods: {
    // Consumption for this hour = last reading this hour − last reading previous hour
    getCellDelta(key: SeriesKey, hourTs: number): number | null {
      const idx = this.hours.indexOf(hourTs)
      if (idx < 0) return null
      const curr = this.hourlyData[key]?.get(hourTs)?.last ?? null
      const prevHourTs = idx > 0 ? this.hours[idx - 1] : (hourTs - HOUR_MS)
      const prev = this.hourlyData[key]?.get(prevHourTs)?.last ?? null
      if (curr === null || prev === null) return null
      const delta = parseFloat((curr - prev).toFixed(2))
      return delta < 0 ? 0 : delta // guard against meter reset / bad data
    },

    cellStyle(key: SeriesKey, hourTs: number): Record<string, string> {
      const delta = this.getCellDelta(key, hourTs)
      if (delta === null) return {}

      const dark = this.isDarkMode
      const { mean, max } = this.deltaStats[key]

      if (delta <= mean) {
        // Green — at or below average consumption
        const ratio = mean > 0 ? delta / mean : 0
        const lightness = dark ? (18 + ratio * 12) : (90 - ratio * 8)
        return {
          backgroundColor: `hsl(120, 55%, ${lightness}%)`,
          color: dark ? '#d1fae5' : '#064e3b'
        }
      }

      // Orange → Red — spike above average
      const range = max - mean
      const ratio = range > 0 ? Math.min((delta - mean) / range, 1) : 1
      const hue = Math.round(35 * (1 - ratio))
      const lightness = dark ? (28 - ratio * 8) : (80 - ratio * 10)
      return {
        backgroundColor: `hsl(${hue}, 80%, ${lightness}%)`,
        color: dark ? '#fef2f2' : '#7f1d1d'
      }
    },

    formatHour(hourTs: number): string {
      return this.showDateInHourLabel
        ? dayjs(hourTs).format('DD/MM HH:mm')
        : dayjs(hourTs).format('HH:mm')
    },
    getHourHeaderId(hourTs: number): string {
      return `wago-energy-heatmap-hour-${hourTs}`
    },
    getSeriesRowId(key: SeriesKey): string {
      return `wago-energy-heatmap-row-${key}`
    },
    getCellId(key: SeriesKey, hourTs: number): string {
      return `wago-energy-heatmap-cell-${key}-${hourTs}`
    },

    formatValue(value: number, unit: string): string {
      return unit ? `${value.toFixed(2)} ${unit}` : value.toFixed(2)
    },

    cellTitle(key: SeriesKey, hourTs: number, unit: string): string {
      const delta = this.getCellDelta(key, hourTs)
      if (delta === null) return '—'
      const date = dayjs(hourTs).format('DD/MM HH:mm')
      const { mean } = this.deltaStats[key]
      const diff = parseFloat((delta - mean).toFixed(2))
      const sign = diff >= 0 ? '+' : ''
      return `${date}  ${this.formatValue(delta, unit)}  (od śr: ${sign}${diff} ${unit})`
    },

    exportCsv(): void {
      if (this.hours.length === 0) return

      const hourHeaders = this.hours.map((h) => dayjs(h).format('YYYY-MM-DD HH:mm'))
      const header = ['Seria', ...hourHeaders].join(',')

      const rows = this.energySeries.map((series) => {
        const cells = this.hours.map((h) => {
          const delta = this.getCellDelta(series.key, h)
          return delta !== null ? delta.toFixed(4) : ''
        })
        return [`"${series.label}"`, ...cells].join(',')
      })

      const csv = [header, ...rows].join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', `energy_heatmap_${dayjs().format('YYYYMMDD_HHmm')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
})
</script>

<style scoped lang="scss">
/* ── Chart header — mirrored from asset-history-view.scss
   (parent styles are scoped and don't reach inside child components) ── */
.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 0;
  margin-bottom: 12px;
}

.chart-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #252422;

  i {
    font-size: 18px;
    color: var(--primary-color);
  }
}

.dark-mode .chart-title {
  color: #ffffff;
}

.chart-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chart-body {
  position: relative;
  padding: 0 20px 20px;
}

.btn-custom {
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-color-rgb), 0.1);
  border: 1px solid rgba(var(--primary-color-rgb), 0.22);
  color: var(--primary-color);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    color: currentColor;
    font-size: 18px;
  }

  &:hover {
    background: rgba(var(--primary-color-rgb), 0.16);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(var(--primary-color-rgb), 0.18);
  }
}

.dark-mode .btn-custom {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  box-shadow: none;
}

/* ── Heatmap body ── */
.heatmap-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.07);

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.05);
    border-radius: 0 0 10px 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 8px;
    opacity: 0.8;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--primary-color);
    opacity: 1;
  }

  scrollbar-width: thin;
  scrollbar-color: var(--primary-color) rgba(15, 23, 42, 0.05);
}

.dark-mode .heatmap-scroll-wrapper {
  border-color: rgba(255, 255, 255, 0.08);

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  scrollbar-color: var(--primary-color) rgba(255, 255, 255, 0.05);
}

.heatmap-table {
  border-collapse: collapse;
  min-width: 100%;
  font-size: 0.91rem;
  white-space: nowrap;
}

.heatmap-table thead tr {
  background: rgba(15, 23, 42, 0.04);
}

.dark-mode .heatmap-table thead tr {
  background: rgba(255, 255, 255, 0.05);
}

.heatmap-table th {
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.88rem;
  text-align: center;
  color: #64748b;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  border-right: 1px solid rgba(15, 23, 42, 0.05);
  letter-spacing: 0.02em;
}

.dark-mode .heatmap-table th {
  color: #94a3b8;
  border-bottom-color: rgba(255, 255, 255, 0.08);
  border-right-color: rgba(255, 255, 255, 0.04);
}

/* Sticky label column */
.heatmap-series-col {
  min-width: 160px;
  text-align: left !important;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 2px solid rgba(15, 23, 42, 0.08) !important;
}

.dark-mode .heatmap-series-col {
  background: #2b2b40;
  border-right-color: rgba(255, 255, 255, 0.1) !important;
}

.heatmap-table thead .heatmap-series-col {
  background: rgba(248, 250, 252, 1);
}

.dark-mode .heatmap-table thead .heatmap-series-col {
  background: rgba(30, 30, 50, 1);
}

.heatmap-series-label {
  font-weight: 600;
  color: #334155;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
}

.dark-mode .heatmap-series-label {
  color: #e2e8f0;
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.heatmap-series-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.heatmap-hour-col {
  min-width: 82px;
}

.heatmap-cell {
  text-align: center;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
  border-right: 1px solid rgba(15, 23, 42, 0.04);
  transition: filter 0.12s;
  cursor: default;
}

.dark-mode .heatmap-cell {
  border-bottom-color: rgba(255, 255, 255, 0.04);
  border-right-color: rgba(255, 255, 255, 0.04);
}

.heatmap-cell:hover {
  filter: brightness(0.88);
  position: relative;
  z-index: 1;
}

.heatmap-cell-value {
  display: block;
  font-weight: 600;
  font-size: 0.88rem;
  line-height: 1.3;
}

.heatmap-no-data {
  font-size: 0.88rem;
  opacity: 0.4;
}

.heatmap-empty {
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-size: 1.00rem;
}
</style>
